import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { studentAPI } from '@/services/api';
import toast from 'react-hot-toast';

const InfoRow = ({ label, value, icon }) => (
  <div className="flex items-start gap-4 p-4 hover:bg-slate-50 rounded-xl transition-colors">
    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 flex-shrink-0">
      <i className={`fas ${icon}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold text-slate-800 mt-0.5 break-words">{value || '—'}</p>
    </div>
  </div>
);

const StudentProfile = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await studentAPI.getProfile();
        setStudent(res.data.data);
      } catch (err) {
        console.error('Profile fetch error:', err);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handlePrintPDF = () => {
    toast.loading('Preparing PDF...', { id: 'print-pdf' });

    const studentName = student?.full_name ||
      `${student?.first_name || ''} ${student?.last_name || ''}`.trim();
    const admissionNo = student?.admission_number || 'N/A';
    const photoUrl = student?.picture || student?.photo ||
                     student?.profile_picture || null;
    const initial = studentName.charAt(0).toUpperCase();
    const printDate = new Date().toLocaleDateString('en-GB', {
      day:'2-digit', month:'long', year:'numeric'
    });
    const dob = student?.date_of_birth
      ? new Date(student.date_of_birth).toLocaleDateString('en-GB', {
          day:'2-digit', month:'long', year:'numeric'
        })
      : 'N/A';

    toast.dismiss('print-pdf');

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Student Card - ${studentName}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body {
    font-family: 'Segoe UI', Roboto, Arial, sans-serif;
    font-size: 14px;
    color: #222;
    background: #ffffff;
    padding: 20px;
    line-height: 1.6;
  }
  .card {
    max-width: 750px;
    margin: 0 auto;
    border: 3px solid #1a5c38;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(26, 92, 56, 0.15);
  }

  /* HEADER */
  .header {
    background: linear-gradient(135deg, #1a5c38 0%, #2d7a4f 100%);
    color: white;
    text-align: center;
    padding: 20px 24px;
  }
  .header .arabic { 
    font-size: 18px; 
    margin-bottom: 6px; 
    opacity: 0.95;
    font-weight: 500;
  }
  .header h1 { 
    font-size: 22px; 
    font-weight: 800; 
    margin-bottom: 3px;
    letter-spacing: 0.5px;
  }
  .header .tagline { 
    font-size: 13px; 
    opacity: 0.9; 
    margin-bottom: 10px;
  }
  .header h2 {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 3px;
    text-transform: uppercase;
    background: rgba(255,255,255,0.2);
    display: inline-block;
    padding: 6px 22px;
    border-radius: 25px;
    margin-top: 6px;
  }

  /* PERSONAL SECTION */
  .personal {
    display: flex;
    gap: 24px;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
    background: #fcfdfc;
  }
  .photo-box {
    flex-shrink: 0;
    width: 110px; 
    height: 135px;
    border: 3px solid #1a5c38;
    border-radius: 8px;
    overflow: hidden;
    background: #e8f5e9;
    display: flex; 
    align-items: center; 
    justify-content: center;
    box-shadow: 0 2px 8px rgba(26, 92, 56, 0.1);
  }
  .photo-box img { 
    width:100%; 
    height:100%; 
    object-fit:cover; 
  }
  .photo-initial { 
    font-size: 52px; 
    font-weight: 900; 
    color: #1a5c38; 
    opacity: 0.35; 
  }
  .photo-label {
    text-align: center;
    font-size: 11px;
    color: #6b7280;
    margin-top: 6px;
    font-weight: 500;
  }
  .photo-col { 
    display: flex; 
    flex-direction: column; 
    align-items: center;
  }
  .details { flex: 1; }
  .section-label {
    background: linear-gradient(135deg, #1a5c38 0%, #2d7a4f 100%);
    color: white;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.8px;
    padding: 6px 14px;
    border-radius: 6px;
    margin-bottom: 12px;
    display: inline-block;
    box-shadow: 0 2px 4px rgba(26, 92, 56, 0.2);
  }
  .field { 
    display: flex; 
    margin-bottom: 8px;
    line-height: 1.6;
  }
  .field-label { 
    font-weight: 700; 
    min-width: 130px; 
    color: #374151;
    font-size: 13px;
  }
  .field-value { 
    color: #111827; 
    font-size: 13px;
    font-weight: 500;
  }
  .field-value.na { 
    color: #9ca3af; 
    font-style: italic; 
  }

  /* OTHER SECTIONS */
  .section {
    padding: 16px 24px;
    border-bottom: 1px solid #e5e7eb;
  }
  .section:last-of-type { border-bottom: none; }
  .section-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 20px;
    margin-top: 10px;
  }
  .section-grid .field { margin-bottom: 6px; }

  /* SIGNATURE */
  .signature-area {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding: 20px 24px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
  }
  .sig-box { 
    text-align: center; 
    width: 220px; 
  }
  .sig-line { 
    border-top: 2px solid #4b5563; 
    padding-top: 8px; 
    font-size: 12px; 
    color: #4b5563; 
    margin-top: 35px;
    font-weight: 500;
  }

  /* FOOTER */
  .footer {
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
    border-top: 1px solid #d1d5db;
    text-align: center;
    padding: 10px;
    font-size: 11px;
    color: #6b7280;
    font-weight: 500;
  }

@media print {
    body { 
      padding: 0; 
      margin: 0;
      width: 100%;
      height: 100vh;
      overflow: hidden;
    }
    @page { 
      size: A4 landscape;
      margin: 0;
    }

    .card { 
      box-shadow: none; 
      max-height: 100vh;
      page-break-after: avoid;
      page-break-inside: avoid;
    }
    .header { padding: 12px 16px; }
    .header h1 { font-size: 20px; }
    .header h2 { font-size: 12px; padding: 4px 16px; }
    .personal { padding: 12px 16px; gap: 16px; }
    .photo-box { width: 110px; height: 135px; }
    .photo-initial { font-size: 52px; }
    .section-label { font-size: 11px; padding: 3px 10px; margin-bottom: 6px; }
    .field { margin-bottom: 4px; font-size: 11.5px; line-height: 1.4; }
    .field-label { min-width: 100px; font-size: 11px; }
    .section { padding: 10px 14px; }
    .section-grid { gap: 4px 14px; }
    .signature-area { padding: 10px 14px; }
    .footer { padding: 6px; font-size: 10px; }

    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  }

</style>
</head>
<body>
<div class="card">

  <!-- HEADER -->
  <div class="header">
    <div class="arabic">بسم الله الرحمن الرحيم</div>
    <h1>DAR-UL-HIKMAH COLLEGE</h1>
    <div class="tagline">of Islamic and Arabic Studies</div>
    <h2>Student Information Card</h2>
  </div>

  <!-- PERSONAL DETAILS -->
  <div class="personal">
    <div class="details">
      <span class="section-label">Personal Details</span>
      <div class="field">
        <span class="field-label">Full Name:</span>
        <span class="field-value">${studentName || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Admission No:</span>
        <span class="field-value">${admissionNo}</span>
      </div>
      <div class="field">
        <span class="field-label">Email:</span>
        <span class="field-value">${student?.email || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Class:</span>
        <span class="field-value">${student?.class || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Date of Birth:</span>
        <span class="field-value">${dob}</span>
      </div>
      <div class="field">
        <span class="field-label">Gender:</span>
        <span class="field-value">${student?.gender || '—'}</span>
      </div>

      <div class="field">
        <span class="field-label">Status:</span>
        <span class="field-value">${(student?.status || '—').toUpperCase()}</span>
      </div>
    </div>
    <div class="photo-col">
      <div class="photo-box">
        ${photoUrl 
          ? `<img src="${photoUrl}" alt="Photo" />`
          : `<div class="photo-initial">${initial}</div>`
        }
      </div>
      <div class="photo-label">Passport Photo</div>
    </div>
  </div>

  <!-- PARENT/GUARDIAN -->
  <div class="section">
    <span class="section-label">Parent / Guardian Information</span>
    <div class="section-grid">
      <div class="field">
        <span class="field-label">Guardian Name:</span>
        <span class="field-value">${student?.parent_name || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Phone:</span>
        <span class="field-value">${student?.parent_phone || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Guardian Email:</span>
        <span class="field-value">${student?.parent_email || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Relationship:</span>
        <span class="field-value">${student?.relationship || '—'}</span>
      </div>
    </div>
    <div class="field" style="margin-top:4px">
      <span class="field-label">Address:</span>
      <span class="field-value">${student?.home_address || '—'}</span>
    </div>
  </div>

  <!-- ENROLLMENT -->
  <div class="section">
    <span class="section-label">Enrollment Information</span>
    <div class="section-grid" style="margin-top:8px">
      <div class="field">
        <span class="field-label">Enrollment Date:</span>
        <span class="field-value">${student?.enrollment_date || student?.created_at || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Payment Status:</span>
        <span class="field-value">${student?.payment_status || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Session:</span>
        <span class="value">${student?.session || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Previous School:</span>
        <span class="field-value">${student?.previous_school || '—'}</span>
      </div>
    </div>
  </div>

  <!-- SIGNATURE -->
  <div class="signature-area">
    <div class="sig-box">
      <div class="sig-line">Principal's Signature & Stamp</div>
    </div>
    <div class="sig-box">
      <div class="sig-line">${printDate}<br/>Date Printed</div>
    </div>
  </div>

  <!-- FOOTER -->
  <div class="footer">
    This document is computer generated by Dar-ul-Hikmah College Management System
  </div>

</div>
<script>
  window.onload = () => setTimeout(() => window.print(), 500);
</script>
</body>
</html>`);
    printWindow.document.close();
  };


  if (loading) {
    return <div className="p-8 text-center">Loading profile...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <button
          onClick={handlePrintPDF}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-all"
        >
          <i className="fas fa-print text-sm" />
          Print Profile
        </button>
        <button
          onClick={() => navigate('/student/edit-profile')}
          className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm flex items-center gap-2"
        >
          <i className="fas fa-pen" />
          Edit Profile
        </button>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        {/* Cover / Avatar Section */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 p-8 text-white relative">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 right-10 w-32 h-32 border-4 border-white rounded-full" />
            <div className="absolute bottom-4 left-10 w-24 h-24 border-4 border-white rounded-full" />
          </div>
          <div className="relative z-10 flex items-center gap-6">
            <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-4xl font-black border-4 border-white/30">
              {(student?.full_name || 'S').charAt(0)}
            </div>
            <div>
              <h3 className="text-2xl font-black">{student?.full_name || 'Student'}</h3>
              <p className="text-emerald-100 font-mono text-sm mt-1">{student?.admission_number || ''}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-semibold backdrop-blur-sm">
                  {student?.class || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Info Sections */}
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-2">
            <InfoRow label="Full Name" value={student?.full_name} icon="fa-user" />
            <InfoRow label="Admission Number" value={student?.admission_number} icon="fa-id-card" />
            <InfoRow label="Email Address" value={student?.email} icon="fa-envelope" />
            <InfoRow label="Phone Number" value={student?.phone} icon="fa-phone" />
            <InfoRow label="Current Class" value={student?.class} icon="fa-chalkboard" />
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
              <i className="fas fa-check-circle" />
            </div>
            <p className="text-sm font-semibold text-slate-600">Payment Status</p>
          </div>
          <p className="text-2xl font-black text-emerald-600 capitalize">{student?.payment_status || 'N/A'}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
              <i className="fas fa-calendar" />
            </div>
            <p className="text-sm font-semibold text-slate-600">Joined</p>
          </div>
          <p className="text-2xl font-black text-slate-800">
            {student?.created_at ? new Date(student.created_at).toLocaleDateString() : 'N/A'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentProfile;


